import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { GetAdminCopilotMaterialsCall } from "../../redux/api/lectureLab.api";
import AdminStatsStudyPlans from "../../prof/stats/AdminStatsStudyPlans";

const headCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

const bodyCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.875rem",
  verticalAlign: "middle",
  borderColor: "divider",
};

const SOURCE_TYPE_LABEL = {
  pdf: "PDF",
  ppt: "PowerPoint",
  image: "Image",
  audio: "Audio",
};

const STATUS_LABEL = {
  queued: "Queued",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
};

function statusChipColor(status) {
  switch (status) {
    case "ready":
      return "success";
    case "processing":
      return "info";
    case "queued":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function sourceTypeChipColor(sourceType) {
  switch (sourceType) {
    case "pdf":
      return "error";
    case "ppt":
      return "warning";
    case "image":
      return "info";
    case "audio":
      return "secondary";
    default:
      return "default";
  }
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(value) {
  if (!value) return "—";
  return moment(value).format("MMM D, YYYY · HH:mm");
}

function initialsFor(user) {
  if (!user) return "?";
  const first = user.firstName?.charAt(0) || "";
  const last = user.lastName?.charAt(0) || "";
  const combo = `${first}${last}`.toUpperCase();
  if (combo) return combo;
  if (user.email) return user.email.charAt(0).toUpperCase();
  return "?";
}

function userDisplayName(user) {
  return (
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    "Unknown user"
  );
}

function progressHint(row) {
  if (row.status === "ready") return "Study pack ready";
  if (row.status === "failed") return row.errorMessage || "Processing failed";
  return row.pipelineLabel || STATUS_LABEL[row.status] || "In progress";
}

function RowMoreMenu({ row, onView }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  return (
    <>
      <IconButton
        size="small"
        aria-label="More actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onView(row.id);
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1.25 }} />
          View study pack
        </MenuItem>
        {row.fileUrl ? (
          <MenuItem
            component="a"
            href={row.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setAnchor(null)}
          >
            <OpenInNewIcon fontSize="small" sx={{ mr: 1.25 }} />
            Open original file
          </MenuItem>
        ) : null}
        {row.user?.email ? (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(row.user.email);
              }
            }}
          >
            Copy user email
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

function MaterialMobileCard({ row, onView }) {
  const user = row.user;
  const fullName = userDisplayName(user);

  return (
    <Paper
      elevation={0}
      onClick={() => onView(row.id)}
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        "&:active": { opacity: 0.92 },
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Avatar
            src={user?.profileImage || undefined}
            alt={fullName}
            sx={{
              width: 40,
              height: 40,
              fontSize: 14,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          >
            {initialsFor(user)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap title={fullName}>
              {fullName}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", wordBreak: "break-all" }}
            >
              {user?.email || "—"}
              {user?.role ? ` · ${user.role}` : ""}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <RowMoreMenu row={row} onView={onView} />
          </Box>
        </Stack>

        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
              lineHeight: 1.35,
            }}
            title={row.title || ""}
          >
            {row.title || "Untitled"}
          </Typography>
          {row.fileName ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.35, wordBreak: "break-all" }}
            >
              {row.fileName}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={SOURCE_TYPE_LABEL[row.sourceType] || row.sourceType || "—"}
            color={sourceTypeChipColor(row.sourceType)}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            size="small"
            label={STATUS_LABEL[row.status] || row.status || "—"}
            color={statusChipColor(row.status)}
            variant={row.status === "failed" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {progressHint(row)}
          {" · "}
          {formatBytes(row.fileSizeBytes)}
          {row.creditsCost != null ? ` · ${row.creditsCost} credits` : ""}
          {" · "}
          {formatWhen(row.createdAt)}
        </Typography>

        <Button
          size="small"
          variant="outlined"
          endIcon={<VisibilityIcon fontSize="small" />}
          onClick={(e) => {
            e.stopPropagation();
            onView(row.id);
          }}
          sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600 }}
        >
          More
        </Button>
      </Stack>
    </Paper>
  );
}

export default function CopilotUsagePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get("section") === "study" ? "study" : "copilot";
  const [section, setSection] = useState(sectionFromUrl);
  const [filterAnchor, setFilterAnchor] = useState(null);

  useEffect(() => {
    setSection(sectionFromUrl);
  }, [sectionFromUrl]);

  const changeSection = (value) => {
    setSection(value);
    const next = new URLSearchParams(searchParams);
    if (value === "study") next.set("section", "study");
    else next.delete("section");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GetAdminCopilotMaterialsCall({
      page,
      limit,
      status: statusFilter || null,
      sourceType: sourceFilter || null,
    }).then(({ materials, pagination: serverMeta }) => {
      if (cancelled) return;
      setRows(Array.isArray(materials) ? materials : []);
      setMeta({
        total: serverMeta?.total || 0,
        totalPages: serverMeta?.totalPages || 1,
      });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page, limit, statusFilter, sourceFilter, refreshTick]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter]);

  const handlePageChange = (_event, newZeroBasedPage) => {
    setPage(newZeroBasedPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10) || 25;
    setLimit(newLimit);
    setPage(1);
  };

  const openDetail = (id) => navigate(`/copilot-usage/${id}`);

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    if (meta.total === 0) return "No Copilot uploads yet.";
    if (meta.total === 1) return "1 Copilot upload";
    return `${meta.total} Copilot uploads`;
  }, [loading, meta.total]);

  const filterControls = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{ width: { xs: "100%", sm: "auto" } }}
    >
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel id="copilot-source-filter">Source type</InputLabel>
        <Select
          labelId="copilot-source-filter"
          label="Source type"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          sx={{ height: 40 }}
        >
          <MenuItem value="">All sources</MenuItem>
          <MenuItem value="pdf">PDF</MenuItem>
          <MenuItem value="ppt">PowerPoint</MenuItem>
          <MenuItem value="image">Image</MenuItem>
          <MenuItem value="audio">Audio</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel id="copilot-status-filter">Status</InputLabel>
        <Select
          labelId="copilot-status-filter"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ height: 40 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="queued">Queued</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="ready">Ready</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </Select>
      </FormControl>
      <Tooltip title="Refresh">
        <IconButton
          onClick={() => setRefreshTick((t) => t + 1)}
          size="small"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 2.5, md: 4 },
          px: { xs: 1.5, sm: 3, md: 4 },
          maxWidth: 1600,
          mx: "auto",
          width: "100%",
          overflowX: "hidden",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-end" }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <MDTypography
                variant="h4"
                fontWeight="bold"
                color="text"
                sx={{ letterSpacing: "-0.02em", fontSize: { xs: "1.5rem", md: "2rem" } }}
              >
                Copilot usage
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{
                  mt: 1,
                  maxWidth: 760,
                  lineHeight: 1.6,
                  opacity: 0.85,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                {section === "study"
                  ? "Study plans people create in the app: who made them, the subject, progress, and the date and time they were created and last used."
                  : "Every PDF, PowerPoint, image or audio file users upload to the Microlab Copilot panel. Latest first. Open More to inspect the generated summary, key structures, quiz, flashcards, topics and progress exactly as in the app."}
              </MDTypography>
              {section === "copilot" ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>

            {section === "study" ? null : isMobile ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={(e) => setFilterAnchor(e.currentTarget)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Filters
                  {statusFilter || sourceFilter ? " · on" : ""}
                </Button>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => setRefreshTick((t) => t + 1)}
                    size="small"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={filterAnchor}
                  open={Boolean(filterAnchor)}
                  onClose={() => setFilterAnchor(null)}
                  PaperProps={{ sx: { p: 2, minWidth: 260 } }}
                >
                  <Stack spacing={1.5}>{filterControls}</Stack>
                </Menu>
              </Stack>
            ) : (
              filterControls
            )}
          </Stack>

          <Tabs
            value={section}
            onChange={(_event, value) => changeSection(value)}
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{ borderBottom: 1, borderColor: "divider", minHeight: 44 }}
          >
            <Tab value="copilot" label="Copilot uploads" sx={{ textTransform: "none", fontWeight: 600 }} />
            <Tab value="study" label="Study plans" sx={{ textTransform: "none", fontWeight: 600 }} />
          </Tabs>

          {section === "study" ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                p: { xs: 1.5, sm: 2 },
                overflow: "hidden",
              }}
            >
              <AdminStatsStudyPlans />
            </Paper>
          ) : null}

          {section === "copilot" ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                boxShadow: (t) =>
                  t.palette.mode === "dark"
                    ? "0 8px 32px rgba(0,0,0,0.35)"
                    : "0 12px 40px rgba(15, 23, 42, 0.06)",
              }}
            >
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress size={36} />
                </Box>
              ) : isMobile ? (
                <Box sx={{ p: 1.5 }}>
                  {rows.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 6 }}
                    >
                      No Copilot uploads match this filter.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {rows.map((row) => (
                        <MaterialMobileCard
                          key={row.id}
                          row={row}
                          onView={openDetail}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="medium" sx={{ minWidth: 960 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headCellSx, minWidth: 220 }}>User</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: 260 }}>Material</TableCell>
                        <TableCell sx={headCellSx}>Source</TableCell>
                        <TableCell sx={headCellSx}>Status / progress</TableCell>
                        <TableCell sx={headCellSx}>Size</TableCell>
                        <TableCell sx={headCellSx}>Credits</TableCell>
                        <TableCell sx={{ ...headCellSx, width: 120 }} align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ ...bodyCellSx, py: 8 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                            >
                              No Copilot uploads match this filter.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row) => {
                          const user = row.user;
                          const fullName = userDisplayName(user);
                          return (
                            <TableRow
                              key={row.id}
                              hover
                              sx={{
                                cursor: "pointer",
                                "&:last-child td": { borderBottom: 0 },
                              }}
                              onClick={() => openDetail(row.id)}
                            >
                              <TableCell sx={bodyCellSx}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Avatar
                                    src={user?.profileImage || undefined}
                                    alt={fullName}
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      fontSize: 14,
                                      bgcolor: "primary.main",
                                    }}
                                  >
                                    {initialsFor(user)}
                                  </Avatar>
                                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap title={fullName}>
                                      {fullName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      title={user?.email || ""}
                                    >
                                      {user?.email || "—"}
                                    </Typography>
                                    {user?.role ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ textTransform: "capitalize", fontWeight: 500 }}
                                      >
                                        {user.role}
                                      </Typography>
                                    ) : null}
                                  </Stack>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ ...bodyCellSx, maxWidth: 360 }}>
                                <Stack spacing={0.25}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      wordBreak: "break-word",
                                    }}
                                    title={row.title || ""}
                                  >
                                    {row.title || "Untitled"}
                                  </Typography>
                                  {row.fileName ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      title={row.fileName}
                                    >
                                      {row.fileName}
                                    </Typography>
                                  ) : null}
                                  <Typography variant="caption" color="text.secondary">
                                    {formatWhen(row.createdAt)}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Chip
                                  size="small"
                                  label={
                                    SOURCE_TYPE_LABEL[row.sourceType] ||
                                    row.sourceType ||
                                    "—"
                                  }
                                  color={sourceTypeChipColor(row.sourceType)}
                                  variant="outlined"
                                  sx={{ fontWeight: 600, textTransform: "none" }}
                                />
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Stack spacing={0.5}>
                                  <Chip
                                    size="small"
                                    label={STATUS_LABEL[row.status] || row.status || "—"}
                                    color={statusChipColor(row.status)}
                                    variant={row.status === "failed" ? "filled" : "outlined"}
                                    sx={{ fontWeight: 600, width: "fit-content" }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {progressHint(row)}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatBytes(row.fileSizeBytes)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Typography variant="body2" color="text.secondary">
                                  {row.creditsCost == null ? "—" : `${row.creditsCost}`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  <Tooltip title="View study pack">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => openDetail(row.id)}
                                      sx={{
                                        border: "1px solid",
                                        borderColor: "primary.light",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <RowMoreMenu row={row} onView={openDetail} />
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Divider />
              <TablePagination
                component="div"
                count={meta.total}
                page={Math.max(page - 1, 0)}
                onPageChange={handlePageChange}
                rowsPerPage={limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={isMobile ? "Per page" : "Uploads per page"}
                sx={{
                  px: { xs: 1, sm: 2 },
                  py: 1,
                  "& .MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    gap: 1,
                    minHeight: { xs: 64, sm: 52 },
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                }}
              />
            </Paper>
          ) : null}
        </Stack>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
